import React, { useState, useEffect } from "react";
import { useParams, Redirect } from "react-router-dom";
import { useSelector } from 'react-redux'
import { useFirestoreConnect, isLoaded, isEmpty, useFirestore, useFirebase } from 'react-redux-firebase'
import { useForm } from "react-hook-form";
import Moment from 'react-moment';

import Page, { statusBreadcrumb } from './Page';
import {PlayerCard} from '../components/Player';
import LinkButton from '../components/LinkButton';
import OnboardingAlert from '../components/OnboardingAlert';
import { useSubmissionSelector, useLazyIsAdmin } from '../hooks';

const capitalize = (text) => text.replace(/^\w/, (c) => c.toUpperCase());

const NextSteps = ({ status }) => {
  if (status === "done") {
    return (
      <p class="done">
        Next steps - We're waiting for your feedback before publishing your podcast.
      </p>
    )
  }

  if (status === "published") {
    return (
      <p class="published">
        Next steps - Nothing left to do, your podcast is published to every podcast player!
      </p>
    )
  }

  if (status === "in_progress") {
    return (
      <p class="in-progress">
        Next steps - We're working on your podcast &amp; we'll share our progress with you soon.
      </p>
    )
  }

  return <></>
}

const PodcastDetails = () => {
  const { podcastId } = useParams();
  const firebase = useFirebase();
  const podcast = useSubmissionSelector(podcastId);
  const profile = useSelector(state => state.firebase.profile)
  const [comments, setComments] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const isAdmin = useLazyIsAdmin();

  const { register, handleSubmit } = useForm();
  const firestore = useFirestore();

  useEffect(() => {
    const loadComments = async () => {
      const snapshot = await firestore
        .collection('submissions')
        .doc(podcastId)
        .collection('comments')
        .orderBy('createdAt', 'asc')
        .get()
      const comments = snapshot.docs.map(doc => {
        const data = doc.data()
        return {...data, createdAt: data.createdAt.toDate()}
      });
      setComments(comments);
    }

    const loadCustomerName = async () => {
      if (!podcast?.userId) {
        return
      }

      const snapshot = await firestore
        .collection('users')
        .doc(podcast.userId)
        .get()

      const user = snapshot.data()
      const name = `${user.name}${user.companyName && ` (${user.companyName})`}`
      setCustomerName(name);
      setCustomerEmail(user.email);
    }

    loadComments();
    loadCustomerName();
  }, [firestore, podcastId, podcast?.userId]);

  useFirestoreConnect([
    {
      collection: 'submissions',
      doc: podcastId
    },
  ]);

  const breadcrumbs = [
    {title: "Podcasts", link: "/podcasts"},
    podcast?.status ? statusBreadcrumb[podcast.status.replace('_', '-')] : {title: "", link: ""},
    {title: podcast?.title, link: ""}
  ]

  const closeOnboardingAlert = () => {
    firebase.updateProfile({hasSeenOnboardingAlert: true})
  }

  const onCommentSubmit = (data, e) => {
    const comment = {
      body: data.comment,
      author: isAdmin ? 'poise' : 'user',
      createdAt: new Date()
    }
    firestore.collection('submissions').doc(podcastId).collection('comments').add({
      ...comment,
      createdAt: firestore.FieldValue.serverTimestamp()
    })
    setComments(oldComments => [...oldComments, comment])
    e.target.reset();
  }

  const action = !isAdmin ? undefined : (
    <LinkButton to={`/podcasts/details/${podcastId}/edit`}>Edit podcast</LinkButton>
  )

  if(!isLoaded(podcast)) {
    return <></>
  }

  if(isLoaded(podcast) && isEmpty(podcast)) {
    return <Redirect to="/" />
  }

  return (
    <Page breadcrumbs={breadcrumbs} action={action}>
      <div class="row justify-content-center">
        <div class="col-12">
          { !profile.hasSeenOnboardingAlert && <OnboardingAlert onClose={closeOnboardingAlert} /> }
          <div class="players">
            <PlayerCard podcast={podcast} showLinks={false} />
          </div>
          <div class="card mt-3">
            <div class="row">
              <div class="col-lg-6">
                <div class="next-steps">
                  <NextSteps status={podcast.status} />
                </div>
                <div class="comments">
                  { comments.length > 0 && <h5 class="pt-3 mb-3">All comments</h5> }
                  { comments.map((comment) => (
                    <div class="comment mb-3">
                      <div class="comment-header pb-1">
                        <span class="author">{comment.author === "poise" ? "Poise"  : "You"}</span>
                      </div>
                      <div class="comment-body">
                        <p>
                          {comment.body}
                        </p>
                        <span class="date">
                          <Moment fromNow>
                            {comment.createdAt && comment.createdAt.toString()}
                          </Moment>
                        </span>
                      </div>
                    </div>
                  ))}
                  <div class="row">
                    <div class="col-lg-10">
                      <form onSubmit={handleSubmit(onCommentSubmit)}>
                        <div class="comment-input pt-3">
                          <h5 class="mb-3">Add a comment</h5>
                           <div class="comment-add">
                             <textarea name="comment" class="form-control" id="" rows="3" ref={register}></textarea>
                           </div>
                        </div>
                        <div class="comment-cta pt-4">
                          <input class="btn btn-primary" type="submit" value="Post comment" ref={register} />
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 offset-lg-2">
                <div class="overview">
                  <h5 class="mb-3">Podcast details</h5>
                  <dl class="mb-0">
                    {
                      podcast.contentType === "url" ? (
                        <>
                          <dt>Content submitted from URL</dt>
                          <dd class="text-muted">
                            <i class="fas fa-angle-right"></i>&nbsp;
                            <a href={ podcast.contentLink } target="blank">View URL</a>
                          </dd>
                        </>
                      ) : (
                        <>
                          <dt>Content submitted from file</dt>
                          <dd class="text-muted">
                            <i class="fas fa-angle-right"></i>&nbsp;
                            <a href={ podcast.contentLink } target="blank">View File</a>
                          </dd>
                        </>
                      )
                    }
                    <dt>Narrator's voice</dt>
                    <dd class="text-muted">{ capitalize(podcast.voice) }</dd>
                    <dt>Editing</dt>
                    <dd class="text-muted">
                      { podcast.contentEdit === "yes" ? (
                        <>
                          { podcast.contentEditUrl ? (
                            <>
                              <i class="fas fa-angle-right"></i>&nbsp;
                              <a href={podcast.contentEditUrl} target="blank">View edit</a>
                            </>
                          ) : (
                            <>Yes</>
                          )}
                        </>
                      ) : (
                        <>No</>
                      )}
                    </dd>
                    { podcast.notes && (
                      <>
                        <dt>Notes</dt>
                        <dd class="text-muted">{ podcast.notes }</dd>
                      </>
                    )}
                    { isAdmin && (
                      <>
                        <dt>Submission ID <i class="fas fa-eye-slash"></i></dt>
                        <dd class="text-muted">{podcastId}</dd>
                        <dt>Customer Name <i class="fas fa-eye-slash"></i></dt>
                        <dd class="text-muted">{customerName || podcast.unauthName}</dd>
                        <dt>Customer Email <i class="fas fa-eye-slash"></i></dt>
                        <dd class="text-muted">{customerEmail || podcast.unauthEmail}</dd>
                      </>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  )
}

export default PodcastDetails;
